// RDS-based Pricing Configuration Service
// This service acts as the new primary interface for pricing data
// It fetches from S3 for clients (fast & reliable) and RDS for admin updates

import { loadPricingConfig as loadStaticPricingConfig } from './pricingConfigService';

// Cache for S3 data
let s3PricingConfig = null;
let lastFetchTime = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Force cache clear function
export function clearPricingCache() {
  s3PricingConfig = null;
  lastFetchTime = null;
  console.log('🧹 Pricing cache cleared');
}

// Flag to track if we're currently fetching data
let isFetching = false;

// Fetch pricing config from S3 (fast & reliable for clients)
async function fetchPricingFromS3() {
  if (isFetching) {
    // Wait for current fetch to complete
    while (isFetching) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return s3PricingConfig;
  }

  try {
    isFetching = true;
    console.log('🔄 Fetching pricing config from S3 (fast & reliable)...');
    
    const response = await fetch('/api/pricing-config', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      
      // Verify this is S3 data
      if (data.source === 'S3' || data.source === 'RDS') {
        s3PricingConfig = data;
        lastFetchTime = Date.now();
        console.log('✅ Pricing config loaded from S3');
        console.log(`📊 Panel mount connectors: ${Object.keys(data.panelMountPricing || {}).length}`);
        console.log(`📊 Inline connectors: ${Object.keys(data.inlinePricing || {}).length}`);
        return s3PricingConfig;
      } else {
        throw new Error('Received invalid pricing data source');
      }
    } else {
      const errorData = await response.text();
      console.error('❌ S3 pricing service unavailable:', response.status, errorData);
      throw new Error(`S3 pricing service unavailable: ${response.status} - ${errorData}`);
    }
  } catch (error) {
    console.error('❌ Error fetching from S3:', error.message);
    throw new Error(`S3 pricing service is currently unavailable: ${error.message}`);
  } finally {
    isFetching = false;
  }
}

// Fetch pricing config from RDS (admin only - for updates)
async function fetchPricingFromRDS() {
  try {
    console.log('🔄 Fetching pricing config from RDS (admin update)...');
    
    const response = await fetch('/api/config-manager?action=pricing', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      
      if (data.source === 'RDS') {
        console.log('✅ Pricing config loaded from RDS for admin update');
        return data;
      } else {
        throw new Error('Received non-RDS pricing data');
      }
    } else {
      const errorData = await response.text();
      console.error('❌ RDS pricing service unavailable:', response.status, errorData);
      throw new Error(`RDS pricing service unavailable: ${response.status} - ${errorData}`);
    }
  } catch (error) {
    console.error('❌ Error fetching from RDS:', error.message);
    throw new Error(`RDS database is currently unavailable: ${error.message}`);
  }
}

// Get pricing configuration (with caching) - S3 for clients, RDS for admin
export async function loadPricingConfig(isAdmin = false) {
  // Check if we have cached data that's still fresh
  if (s3PricingConfig && lastFetchTime && (Date.now() - lastFetchTime) < CACHE_DURATION) {
    return s3PricingConfig;
  }

  // Fetch fresh data - S3 for clients, RDS for admin
  try {
    if (isAdmin) {
      return await fetchPricingFromRDS();
    } else {
      return await fetchPricingFromS3();
    }
  } catch (error) {
    console.error('❌ Cannot load pricing config:', error.message);
    
    // For admin, we need RDS - no fallback
    if (isAdmin) {
      throw error;
    }
    
    // For clients, try static fallback
    console.warn('⚠️ Falling back to static pricing config for client');
    return await loadStaticPricingConfig();
  }
}

// Update connector pricing (admin function) - updates RDS, triggers S3 sync
export async function updateConnectorPricing(connectorId, pricing, isPanelMount = false) {
  try {
    const { getAdminToken } = await import('./authService');
    const token = getAdminToken();
    const user = localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')).username : 'unknown';
    
    const response = await fetch('/api/config-manager?action=pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        'user-id': user
      },
      body: JSON.stringify({
        connectorId,
        mountType: isPanelMount ? 'panel' : 'inline',
        partCost: pricing.partCost,
        laborCost: pricing.laborCost || 2.50
      })
    });

    if (response.ok) {
      // Clear cache to force refresh
      s3PricingConfig = null;
      lastFetchTime = null;
      console.log(`✅ Updated connector pricing: ${connectorId} (${isPanelMount ? 'panel' : 'inline'})`);
      
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Failed to update connector pricing:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error updating connector pricing:', error);
    return false;
  }
}

// Update cable base pricing (admin function) - updates RDS, triggers S3 sync
export async function updateCableBasePrices(cableType, price, wireGauge = null) {
  try {
    const { getAdminToken } = await import('./authService');
    const token = getAdminToken();
    const user = localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')).username : 'unknown';
    
    const response = await fetch('/api/config-manager?action=pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        'user-id': user
      },
      body: JSON.stringify({
        cableType,
        pricePerFoot: price,
        wireGauge
      })
    });

    if (response.ok) {
      // Clear cache to force refresh
      s3PricingConfig = null;
      lastFetchTime = null;
      console.log(`✅ Updated cable pricing: ${cableType}${wireGauge ? ` (${wireGauge})` : ''}`);
      
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Failed to update cable pricing:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error updating cable pricing:', error);
    return false;
  }
}

// Bulk update pricing (admin function) - updates RDS, triggers S3 sync
export async function bulkUpdatePricing(updates) {
  try {
    const { getAdminToken } = await import('./authService');
    const token = getAdminToken();
    const user = localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')).username : 'unknown';
    
    const response = await fetch('/api/config-manager?action=bulk-update', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        'user-id': user
      },
      body: JSON.stringify({
        type: 'pricing',
        data: updates
      })
    });

    if (response.ok) {
      const result = await response.json();
      // Clear cache to force refresh
      s3PricingConfig = null;
      lastFetchTime = null;
      console.log(`✅ Bulk updated pricing: ${result.message}`);
      
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Failed to bulk update pricing:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error bulk updating pricing:', error);
    return false;
  }
}

// Save pricing config (for backward compatibility) - updates RDS, triggers S3 sync
export async function savePricingConfig(config) {
  console.log('📝 Saving pricing config via RDS (triggers S3 sync)...');
  
  // Convert the config to individual updates
  const updates = [];
  
  // Panel mount pricing
  if (config.panelMountPricing) {
    Object.entries(config.panelMountPricing).forEach(([connectorId, pricing]) => {
      updates.push({
        connectorId,
        mountType: 'panel',
        partCost: pricing.partCost,
        laborCost: pricing.laborCost
      });
    });
  }
  
  // Inline pricing
  if (config.inlinePricing) {
    Object.entries(config.inlinePricing).forEach(([connectorId, pricing]) => {
      updates.push({
        connectorId,
        mountType: 'inline',
        partCost: pricing.partCost,
        laborCost: pricing.laborCost
      });
    });
  }
  
  // Cable base prices
  if (config.cableBasePrices) {
    Object.entries(config.cableBasePrices).forEach(([cableType, price]) => {
      updates.push({
        cableType,
        pricePerFoot: price
      });
    });
  }
  
  // Power wire gauge pricing
  if (config.powerWireGaugePricing) {
    Object.entries(config.powerWireGaugePricing).forEach(([wireGauge, price]) => {
      updates.push({
        cableType: 'power',
        wireGauge,
        pricePerFoot: price
      });
    });
  }
  
  // Wye cable pricing
  if (config.wyeCablePricing) {
    Object.entries(config.wyeCablePricing).forEach(([cableType, price]) => {
      updates.push({
        cableType: `wye_${cableType}`,
        pricePerFoot: price
      });
    });
  }
  
  return await bulkUpdatePricing(updates);
}

// Initialize the pricing system
export async function initializePricingSystem() {
  console.log('🔧 Initializing S3-based pricing system...');
  try {
    await fetchPricingFromS3();
    console.log('🚀 S3-based pricing system ready!');
  } catch (error) {
    console.warn('⚠️ S3 pricing initialization warning:', error.message);
  }
}

// Force refresh from S3 (for admin use)
export async function refreshPricingConfig() {
  console.log('🔄 Force refreshing pricing config from S3...');
  s3PricingConfig = null;
  lastFetchTime = null;
  return await fetchPricingFromS3();
}

// Export pricing config (for admin dashboard)
export async function exportPricingConfig() {
  const config = await loadPricingConfig();
  return {
    ...config,
    exportDate: new Date().toISOString(),
    source: s3PricingConfig ? 'S3' : 'static'
  };
}

// Get pricing history (placeholder - would need to be implemented in RDS)
export async function getPricingHistory() {
  // This would need to be implemented in the RDS API
  console.log('📈 Pricing history not yet implemented in RDS');
  return [];
}

// Health check
export async function checkPricingSystemHealth() {
  try {
    const response = await fetch('/api/config-manager?action=health');
    if (response.ok) {
      const health = await response.json();
      return {
        status: 'healthy',
        source: s3PricingConfig ? 'S3' : 'static',
        lastFetch: lastFetchTime ? new Date(lastFetchTime).toISOString() : 'never',
        cacheValid: s3PricingConfig && lastFetchTime && (Date.now() - lastFetchTime) < CACHE_DURATION,
        rdsHealth: health
      };
    }
    return { status: 'unhealthy', source: 'static' };
  } catch (error) {
    return { status: 'error', source: 'static', error: error.message };
  }
}

// RDS-based implementations of pricing functions (admin only)
export async function updatePricingSection(section, data) {
  try {
    const { getAdminToken } = await import('./authService');
    const token = getAdminToken();
    const user = localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')).username : 'unknown';
    
    const response = await fetch('/api/config-manager?action=pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        'user-id': user
      },
      body: JSON.stringify({
        section,
        data
      })
    });

    if (response.ok) {
      // Clear cache to force refresh
      s3PricingConfig = null;
      lastFetchTime = null;
      console.log(`✅ Updated pricing section: ${section}`);
      
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Failed to update pricing section:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error updating pricing section:', error);
    return false;
  }
}

export async function updatePowerWireGaugePricing(gauge, price) {
  try {
    const { getAdminToken } = await import('./authService');
    const token = getAdminToken();
    const user = localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')).username : 'unknown';
    
    const response = await fetch('/api/config-manager?action=pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        'user-id': user
      },
      body: JSON.stringify({
        cableType: 'power',
        wireGauge: gauge,
        pricePerFoot: price
      })
    });

    if (response.ok) {
      // Clear cache to force refresh
      s3PricingConfig = null;
      lastFetchTime = null;
      console.log(`✅ Updated power wire gauge pricing: ${gauge}`);
      
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Failed to update power wire gauge pricing:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error updating power wire gauge pricing:', error);
    return false;
  }
}

export async function updateWyeCablePricing(cableType, price) {
  try {
    const { getAdminToken } = await import('./authService');
    const token = getAdminToken();
    const user = localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')).username : 'unknown';
    
    const response = await fetch('/api/config-manager?action=pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        'user-id': user
      },
      body: JSON.stringify({
        cableType: `wye_${cableType}`,
        pricePerFoot: price
      })
    });

    if (response.ok) {
      // Clear cache to force refresh
      s3PricingConfig = null;
      lastFetchTime = null;
      console.log(`✅ Updated wye cable pricing: ${cableType}`);
      
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Failed to update wye cable pricing:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error updating wye cable pricing:', error);
    return false;
  }
}

export async function updateFinishingPricing(option, price) {
  try {
    const { getAdminToken } = await import('./authService');
    const token = getAdminToken();
    const user = localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')).username : 'unknown';
    
    const response = await fetch('/api/config-manager?action=pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        'user-id': user
      },
      body: JSON.stringify({
        category: 'finishing',
        option,
        price
      })
    });

    if (response.ok) {
      // Clear cache to force refresh
      s3PricingConfig = null;
      lastFetchTime = null;
      console.log(`✅ Updated finishing pricing: ${option}`);
      
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Failed to update finishing pricing:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error updating finishing pricing:', error);
    return false;
  }
}

export async function updateLaborCosts(quality, cost) {
  try {
    const { getAdminToken } = await import('./authService');
    const token = getAdminToken();
    const user = localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')).username : 'unknown';
    
    const response = await fetch('/api/config-manager?action=pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        'user-id': user
      },
      body: JSON.stringify({
        category: 'labor',
        quality,
        cost
      })
    });

    if (response.ok) {
      // Clear cache to force refresh
      s3PricingConfig = null;
      lastFetchTime = null;
      console.log(`✅ Updated labor costs: ${quality}`);
      
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Failed to update labor costs:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error updating labor costs:', error);
    return false;
  }
}

export async function updateMarkups(category, markup) {
  try {
    const { getAdminToken } = await import('./authService');
    const token = getAdminToken();
    const user = localStorage.getItem('currentUser') ? JSON.parse(localStorage.getItem('currentUser')).username : 'unknown';
    
    const response = await fetch('/api/config-manager?action=pricing', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token,
        'user-id': user
      },
      body: JSON.stringify({
        category: 'markup',
        markupCategory: category,
        markup
      })
    });

    if (response.ok) {
      // Clear cache to force refresh
      s3PricingConfig = null;
      lastFetchTime = null;
      console.log(`✅ Updated markup: ${category}`);
      
      return true;
    } else {
      const error = await response.json();
      console.error('❌ Failed to update markup:', error);
      return false;
    }
  } catch (error) {
    console.error('❌ Error updating markup:', error);
    return false;
  }
}

// Re-export static-only functions for backward compatibility
export {
  importPricingConfig,
  resetPricingConfig,
  addPricingHistoryEntry
} from './pricingConfigService';